import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { FiCheckCircle } from 'react-icons/fi';
import ReactTooltip from 'react-tooltip';
import Box from 'common/components/box/Box';
import LoadingSpinner from 'common/components/loading/LoadingSpinner';
import { AssetAboveConsumptionDashboardDto } from 'api/dashboard/dtos/AssetAboveConsumptionDashboardDto';
import DashboardService from 'api/dashboard/DashboardService';
import { FiltersFormModel } from '../filters/FiltersView';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import useWidgetFilters from '../hooks/useWidgetFilters';
import WidgetMonthPicker from '../components/WidgetMonthPicker';
import useInfiniteReveal from '../components/useInfiniteReveal';
import tableStyles from '../dashboardTable/DashboardTable.module.scss';
import styles from './AssetsAboveConsumption.module.scss';
import infiniteStyles from '../components/useInfiniteReveal.module.scss';

const PAGE_SIZE = 15;

const formatConsumption = (value: number) => {
	return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' L/100km';
};

type Props = {
	filters?: FiltersFormModel;
	vehicles?: SelectValueLabel[];
};

const AssetsAboveConsumption = (props: Props) => {
	const { t } = useTranslation();
	const { addToast } = useToasts();
	const [data, setData] = useState<AssetAboveConsumptionDashboardDto[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	const { dateFrom, dateTo, setDateRange } = useWidgetFilters('assetsAboveConsumption');

	useEffect(() => {
		let cancelled = false;
		const fetchData = async () => {
			setIsLoading(true);
			try {
				const criteria = props.filters
					? { ...props.filters }
					: { dateFrom, dateTo };
				const result = await DashboardService.getAssetsAboveConsumption(criteria);
				if (!cancelled) {
					setData(result || []);
				}
			} catch {
				if (!cancelled) {
					addToast(t('common.messages.error_load_info'), { appearance: 'error' });
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false);
				}
			}
		};
		fetchData();
		return () => { cancelled = true; };
	}, [props.filters, dateFrom, dateTo]);

	const { visibleItems, sentinelRef, hasMore } = useInfiniteReveal(data, PAGE_SIZE);

	const buildVehicleTooltip = (item: AssetAboveConsumptionDashboardDto) =>
		[item.vehicleBrandName, item.vehicleModelName].filter(Boolean).join(' ') || undefined;

	const duplicatePlateIds = useMemo(() => {
		const counts = new Map<string, number>();
		data.forEach(it => counts.set(it.vehicleId, (counts.get(it.vehicleId) || 0) + 1));
		const dup = new Set<string>();
		counts.forEach((count, id) => { if (count > 1) dup.add(id); });
		return dup;
	}, [data]);

	return (
		<Box
			className={tableStyles.container}
			title={t('dashboard.assets_above_consumption') as string}
			subTitleTooltip={t('dashboard.assets_above_consumption_tooltip' as any) as string}
			renderOptions={<WidgetMonthPicker dateFrom={dateFrom} dateTo={dateTo} onChange={setDateRange} />}
		>
			{isLoading ? (
				<div className={tableStyles.centerAlign}>
					<LoadingSpinner />
				</div>
			) : data.length === 0 ? (
				<div className={styles.emptyState}>
					<div className={styles.emptyIcon}>
						<FiCheckCircle />
					</div>
					<div className={styles.emptyMessage}>
						{t('dashboard.assets_above_consumption_all_within_ref' as any) as string}
					</div>
				</div>
			) : (
				<>
				<div className={styles.tableWrapper}>
					<table className={styles.table}>
						<thead>
							<tr>
								<th>{t('dashboard.columns.vehicle')}</th>
								<th>{t('dashboard.columns.driver' as any)}</th>
								<th className={styles.textRight}>{t('dashboard.assets_above_consumption_columns.reference' as any)}</th>
								<th className={styles.textRight}>{t('dashboard.assets_above_consumption_columns.actual' as any)}</th>
								<th className={styles.textRight}>{t('dashboard.assets_above_consumption_columns.excess' as any)}</th>
							</tr>
						</thead>
						<tbody>
							{visibleItems.map(item => {
								const showFuel = duplicatePlateIds.has(item.vehicleId);
								return (
									<tr
										key={`${item.vehicleId}-${item.fuelId}`}
										className={styles.tableRow}
										onClick={() => window.open(`/vehicles/details/${item.vehicleId}/monthlyCharges/list`, '_blank')}
									>
										<td>
											<span className={styles.plate} data-tip={buildVehicleTooltip(item)} data-for="assets-above-consumption-tooltip" data-place="right">{item.registrationNumber}</span>
											{showFuel && <span className={styles.fuelTag}> · {item.fuelName}</span>}
										</td>
										<td>{item.driverName || <span className={styles.muted}>—</span>}</td>
										<td className={styles.textRight}>{formatConsumption(item.referenceConsumption)}</td>
										<td className={styles.textRight}>{formatConsumption(item.actualConsumption)}</td>
										<td className={styles.textRight}>
											<span className={`${styles.badge} ${styles.badgeRed}`}>
												+{formatConsumption(item.excess)}
											</span>
										</td>
									</tr>
								);
							})}
							{hasMore && (
								<tr ref={sentinelRef as unknown as React.Ref<HTMLTableRowElement>}>
									<td colSpan={5} className={infiniteStyles.sentinelRow} />
								</tr>
							)}
						</tbody>
					</table>
				</div>
				<ReactTooltip id="assets-above-consumption-tooltip" className={styles.tooltip} effect="solid" multiline={true} />
				</>
			)}
		</Box>
	);
};

export default AssetsAboveConsumption;
